import { useCallback } from 'react'

import { apiUrl, toQuery, useApiRequest } from '../useApiRequest'

import { PriceRule } from '../../types/booking/booking'

/**
 * Single-session price rules. Editing creates a new version (bookings keep the version they were quoted with).
 */
export function usePriceRules() {
  const { run, loading, error, resetState } = useApiRequest()

  const list = useCallback(
    (clubId?: number) =>
      run(({ fetcher }) => fetcher<PriceRule[]>(apiUrl(`/price-rules${toQuery({ clubId })}`))),
    [run]
  )

  /** Every version of the rule the given version belongs to, newest first. */
  const versions = useCallback(
    (id: number) => run(({ fetcher }) => fetcher<PriceRule[]>(apiUrl(`/price-rules/${id}/versions`))),
    [run]
  )

  const create = useCallback(
    (rule: PriceRule) =>
      run(({ poster }) => poster<PriceRule>(apiUrl('/price-rules'), rule, 'application/json')),
    [run]
  )

  const update = useCallback(
    (id: number, rule: PriceRule) =>
      run(({ putter }) => putter<PriceRule>(apiUrl(`/price-rules/${id}`), rule, 'application/json')),
    [run]
  )

  const archive = useCallback(
    (id: number) => run(({ deleter }) => deleter<void>(apiUrl(`/price-rules/${id}`))),
    [run]
  )

  return { list, versions, create, update, archive, loading, error, resetState }
}
